import React, { useMemo, useState } from 'react';

import { Box, Text, Typography } from '@rbilabs/universal-components';
import { StyleSheet } from 'react-native';

import { INutritionInfo } from '@rbi-ctg/menu';
import NutritionModal from 'components/nutrition';
import { Allergens } from 'components/nutrition/allergens';
import { AllergenGroup, NutritionFacts } from 'components/nutrition/types';
import { getAllergenGroups } from 'components/nutrition/utils';
import { IIngredients } from 'generated/sanity-graphql';
import { useFormatCalories } from 'hooks/use-format-calories';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';

import { Card } from './card';
import { IProductCard } from './types';

const Body = Box.withConfig({
  flex: 1,
  paddingY: {
    base: '$2',
    md: '$3',
  },
  paddingX: {
    base: '$3',
    md: '$4',
  },
});

const CaloriesContainer = Text.withConfig({
  margin: 0,
  variant: 'copyTwo',
  textAlign: 'center',
  marginBottom: '$4',
});

const ProductCard: React.FC<React.PropsWithChildren<IProductCard>> = ({ product, size }) => {
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);

  const enableAllergens = useFlag(LaunchDarklyFlag.ENABLE_NUTRITION_EXPLORER_ALLERGENS);
  const formatCalories = useFormatCalories();

  // We should always use the nutritionWithModifiers for the nutrition text
  const nutritionText = useMemo(() => formatCalories(product.nutritionWithModifiers.calories), [
    formatCalories,
    product.nutritionWithModifiers.calories,
  ]);

  let allergenGroups: AllergenGroup[] | undefined;
  if (enableAllergens && product.allergens) {
    allergenGroups = getAllergenGroups(product.allergens);
  }

  const nutritionFacts: NutritionFacts[] = [
    {
      itemName: product.name,
      itemImage: product.image,
      facts: product.nutritionWithModifiers || ({} as INutritionInfo),
      allergenGroups,
      ingredients: (product?.ingredients || []) as IIngredients[],
    },
  ];

  const nutritionInfoCard = (
    <Card key={product._id} image={product.image} size={size} onPress={() => setIsModalOpen(true)}>
      <Typography variant="headerTwo" accessibilityLevel={3} style={styles.title}>
        {product.name}
      </Typography>
      <Body>
        {nutritionText && <CaloriesContainer>{nutritionText}</CaloriesContainer>}
        {allergenGroups && <Allergens allergenGroups={allergenGroups} />}
      </Body>
    </Card>
  );

  return (
    <>
      {nutritionInfoCard}
      {isModalOpen && (
        <NutritionModal nutritionFacts={nutritionFacts} onDismiss={() => setIsModalOpen(false)} />
      )}
    </>
  );
};

export default React.memo(ProductCard);

const styles = StyleSheet.create({
  title: {
    margin: 0,
    letterSpacing: 1,
    textTransform: 'none',
    textAlign: 'center',
  },
});
